import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import paypalLogo from "./assets/payment/paypal.png";
import venmoLogo from "./assets/payment/venmo.png";
import amazonGiftCardLogo from "./assets/payment/amazon.png";
import visaGiftCardLogo from "./assets/payment/visa.svg";
import BACKEND_URL from "./config";
import { trackLinkedInConversion } from "./linkedin-ad-conversion-tracking/linkedin";
import {
  LINKEDIN_CONV_ID_LANDING_VIEWED,
  LINKEDIN_CONV_ID_REMINDER_ADDED,
  LINKEDIN_CONV_ID_ELIGIBILITY_PASSED,
  LINKEDIN_CONV_ID_OVERVIEW_CONTINUED,
  LINKEDIN_CONV_ID_ABOUT_FORM_VIEWED,
  LINKEDIN_CONV_ID_PAYMENT_METHOD_SELECTED,
  LINKEDIN_CONV_ID_START_INTERVIEW_CLICKED,
  LINKEDIN_CONV_ID_INTERVIEW_STARTED,
} from "./linkedin-ad-conversion-tracking/conversionIds";
import {
  getVisitorId,
  isMobileOrTabletDevice,
  trackFunnel,
} from "./funnel/tracking";
import { colors, ui, Card, InlineAlert, Chip, Field } from "./participant/ui";

const COMPANY_SIZE_OPTIONS = [
  "Myself only",
  "2-10 employees",
  "11-50 employees",
  "51-200 employees",
  "201-500 employees",
  "501-1,000 employees",
  "1,001-5,000 employees",
  "5,001-10,000 employees",
  "10,001+ employees",
];

// Two screens (down from five in 2026-08 UX refresh — see AI_INTERVIEWER_UX_PLAN.md).
// Every funnel event from the old five-step flow still fires at an equivalent
// moment; the enum in funnel/tracking.js is untouched.
// 0 = Welcome (old Landing + Eligibility + Overview)
// 1 = Your details (old About you + Payment & consent)
const STEP_WELCOME = 0;
const STEP_DETAILS = 1;

export default function InterviewLandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const topRef = useRef(null);

  // Track step views only once per page load
  const trackedRef = useRef({
    landingViewed: false,
    aboutViewed: false,
    aboutCompleted: false,
    eligibilityPassed: false,
    mobileBlocked: false,
  });

  const [loading, setLoading] = useState(true);
  const [interviewType, setInterviewType] = useState(null);
  const [error, setError] = useState("");

  const [step, setStep] = useState(STEP_WELCOME);

  // Eligibility state
  // "" | "yes" | "no"
  const [recentUseAnswer, setRecentUseAnswer] = useState("");

  const [useInterviewEmailForPayment, setUseInterviewEmailForPayment] =
    useState(true);

  const [form, setForm] = useState({
    interviewee_name: "",
    interviewee_email: "",
    company_name: "",
    job_title: "",
    company_size: "",
    decision_involvement: "",
    payment_method: "", // paypal | venmo | amazon_gift_card | visa_gift_card
    payment_email: "",
    // Consent is two checkboxes (was four across two screens). ack_ready covers
    // both "requirements understood" and "ready to speak"; the submit payload's
    // acknowledgements shape is unchanged.
    ack_ready: false,
    consent_recording: false,
  });

  const payout = useMemo(() => interviewType?.payout_amount, [interviewType]);
  const estimatedMinutes = useMemo(
    () => interviewType?.estimated_minutes || 45,
    [interviewType]
  );

  // Evaluated once: the device does not change mid-visit, and re-running it on
  // every render would recompute a regex for nothing.
  const isMobile = useMemo(() => isMobileOrTabletDevice(), []);

  const focusType = useMemo(() => {
    return (interviewType?.interview_focus_type || "product").toLowerCase();
  }, [interviewType]);

  const productName = useMemo(() => {
    // Backwards compatible:
    // - New backend returns "topic_name" (product_name or jtbd_name)【routes 2.py】
    // - Old types may only have product_name/name
    const topic =
      interviewType?.topic_name ||
      (focusType === "jtbd" ? interviewType?.jtbd_name : interviewType?.product_name) ||
      interviewType?.product_name ||
      interviewType?.name;

    return topic || (focusType === "jtbd" ? "this workflow" : "this product");
  }, [interviewType, focusType]);

  const paymentCopy = useMemo(() => {
    const m = form.payment_method;
    if (m === "paypal") {
      return {
        label: "PayPal email",
        placeholder: "Email on your PayPal account",
      };
    }
    if (m === "venmo") {
      return {
        label: "Venmo email or phone",
        placeholder: "Email or phone on your Venmo account",
      };
    }
    if (m === "amazon_gift_card" || m === "visa_gift_card") {
      return {
        label: "Email for gift card delivery",
        placeholder: "Email to send the gift card to",
      };
    }
    return {
      label: "Payment account email",
      placeholder: "Select a payment method first",
    };
  }, [form.payment_method]);

  function pad2(n) {
    return String(n).padStart(2, "0");
  }

  function toIcsUtc(dt) {
    // Format: YYYYMMDDTHHMMSSZ
    return (
      dt.getUTCFullYear() +
      pad2(dt.getUTCMonth() + 1) +
      pad2(dt.getUTCDate()) +
      "T" +
      pad2(dt.getUTCHours()) +
      pad2(dt.getUTCMinutes()) +
      pad2(dt.getUTCSeconds()) +
      "Z"
    );
  }

  function getDefaultReminderTimes() {
    // Tomorrow at 10:00am local time, 15-min duration
    const now = new Date();
    const startLocal = new Date(now);
    startLocal.setDate(now.getDate() + 1);
    startLocal.setHours(10, 0, 0, 0);

    const endLocal = new Date(startLocal);
    endLocal.setMinutes(endLocal.getMinutes() + 15);

    return { startLocal, endLocal };
  }

  function getLandingUrl() {
    if (typeof window === "undefined") return "";
    return window.location.href;
  }

  function buildReminderCopy() {
    const link = getLandingUrl();
    const title = `Paid AI interview (10 min) — $${payout || 20} payout`;
    const details =
      `Interview topic: ${productName}\n\n` +
      `Link: ${link}\n\n` +
      `Limited availability — complete soon.\n\n` +
      `Privacy & safety:\n` +
      `- Every recording is reviewed by a person before it is shared with anyone\n` +
      `- You control what’s on screen — close unrelated tabs and avoid showing customer names or account numbers\n` +
      `- You’ll have 48 hours after processing to request edits or full removal\n`;

    return { title, details, link };
  }

  function downloadIcsReminder() {
    const { startLocal, endLocal } = getDefaultReminderTimes();
    const { title, details } = buildReminderCopy();

    const uid = `${Date.now()}-${Math.random().toString(16).slice(2)}@conversation-intelligence.com`;
    const dtStart = toIcsUtc(new Date(startLocal));
    const dtEnd = toIcsUtc(new Date(endLocal));
    const dtStamp = toIcsUtc(new Date());

    const ics =
      "BEGIN:VCALENDAR\n" +
      "VERSION:2.0\n" +
      "PRODID:-//Product Intelligence Network//AI Interview Reminder//EN\n" +
      "CALSCALE:GREGORIAN\n" +
      "METHOD:PUBLISH\n" +
      "BEGIN:VEVENT\n" +
      `UID:${uid}\n` +
      `DTSTAMP:${dtStamp}\n` +
      `DTSTART:${dtStart}\n` +
      `DTEND:${dtEnd}\n` +
      `SUMMARY:${title.replace(/\n/g, " ")}\n` +
      `DESCRIPTION:${details.replace(/\n/g, "\\n")}\n` +
      "END:VEVENT\n" +
      "END:VCALENDAR\n";

    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "paid-ai-interview-reminder.ics";
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(url);
  }

  function openGoogleCalendar() {
    const { startLocal, endLocal } = getDefaultReminderTimes();
    const { title, details, link } = buildReminderCopy();

    // Google expects: YYYYMMDDTHHMMSSZ/YYYYMMDDTHHMMSSZ in UTC
    const startUtc = toIcsUtc(new Date(startLocal));
    const endUtc = toIcsUtc(new Date(endLocal));
    const dates = `${startUtc}/${endUtc}`;

    const url =
      "https://calendar.google.com/calendar/render?action=TEMPLATE" +
      `&text=${encodeURIComponent(title)}` +
      `&details=${encodeURIComponent(details)}` +
      `&location=${encodeURIComponent(link)}` +
      `&dates=${encodeURIComponent(dates)}`;

    window.open(url, "_blank", "noopener,noreferrer");
  }

  function openOutlookCalendar() {
    const { startLocal, endLocal } = getDefaultReminderTimes();
    const { title, details, link } = buildReminderCopy();

    // Outlook deep link uses ISO strings (local->UTC in toISOString)
    const startIso = new Date(startLocal).toISOString();
    const endIso = new Date(endLocal).toISOString();

    const url =
      "https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent" +
      `&subject=${encodeURIComponent(title)}` +
      `&body=${encodeURIComponent(details)}` +
      `&location=${encodeURIComponent(link)}` +
      `&startdt=${encodeURIComponent(startIso)}` +
      `&enddt=${encodeURIComponent(endIso)}`;

    window.open(url, "_blank", "noopener,noreferrer");
  }

  function handleReminderClick(kind) {
    // Track reminder intent
    trackFunnel("reminder_added", { kind, payout_shown: payout ?? null }, { slug });
    trackLinkedInConversion(LINKEDIN_CONV_ID_REMINDER_ADDED);

    if (kind === "google") return openGoogleCalendar();
    if (kind === "outlook") return openOutlookCalendar();
    return downloadIcsReminder();
  }

  // Clear decision_involvement if focusType is jtbd
  useEffect(() => {
    if (focusType === "jtbd" && form.decision_involvement) {
      setForm((prev) => ({ ...prev, decision_involvement: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusType]);

  // Record the visitors we turn away at the door. Without this they would be
  // invisible — indistinguishable from people who never loaded the page — and
  // the size of the wasted-spend problem would stay unknowable.
  useEffect(() => {
    if (!interviewType) return;
    if (!isMobile) return;
    if (trackedRef.current.mobileBlocked) return;

    trackedRef.current.mobileBlocked = true;
    trackFunnel(
      "mobile_blocked",
      { payout_shown: payout ?? null },
      { slug }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewType, isMobile]);

  // Fire "Landing Viewed" once when the welcome screen is shown and the interview type is loaded
  useEffect(() => {
    if (!interviewType) return;
    if (isMobile) return; // turned away — never entered the funnel
    if (step !== STEP_WELCOME) return;
    if (trackedRef.current.landingViewed) return;

    trackedRef.current.landingViewed = true;
    // payout_shown is recorded here and at every later decision point: it is
    // what this visitor was actually offered, which is the only way a price
    // change is interpretable after the fact.
    trackFunnel(
      "landing_viewed",
      { payout_shown: payout ?? null, estimated_minutes: estimatedMinutes },
      { slug }
    );
    trackLinkedInConversion(LINKEDIN_CONV_ID_LANDING_VIEWED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewType, step]);

  // Fire "About Form Viewed" once when the details screen is first shown
  useEffect(() => {
    if (step !== STEP_DETAILS) return;
    if (trackedRef.current.aboutViewed) return;

    trackedRef.current.aboutViewed = true;
    trackFunnel("about_form_viewed", { payout_shown: payout ?? null }, { slug });
    trackLinkedInConversion(LINKEDIN_CONV_ID_ABOUT_FORM_VIEWED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // "About Form Completed" used to fire on a Next button between the About and
  // Payment screens. Those screens are now one form, so it fires the moment the
  // about fields first become valid — the same user moment, without the click.
  // This is where an anonymous browser becomes a person: the email stitches the
  // whole prior trace to a real identity. No LinkedIn counterpart (never was).
  useEffect(() => {
    if (step !== STEP_DETAILS) return;
    if (trackedRef.current.aboutCompleted) return;
    if (missingAbout().length > 0) return;

    trackedRef.current.aboutCompleted = true;
    trackFunnel(
      "about_form_completed",
      {
        email: form.interviewee_email.trim(),
        job_title: form.job_title.trim(),
        company_size: form.company_size,
        payout_shown: payout ?? null,
      },
      { slug }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, form]);

  // Auto-fill payment_email from interviewee_email if toggle is on
  useEffect(() => {
    // Only autofill when:
    // - user has chosen a payment method (so the field is active/meaningful)
    // - the toggle is ON
    // - payment_email is currently blank (so we don't overwrite user edits)
    const interviewEmail = (form.interviewee_email || "").trim();

    if (!form.payment_method) return;
    if (!useInterviewEmailForPayment) return;
    if (form.payment_email.trim()) return;

    if (interviewEmail.includes("@")) {
      setForm((prev) => ({ ...prev, payment_email: interviewEmail }));
    }
  }, [
    form.payment_method,
    useInterviewEmailForPayment,
    form.interviewee_email,
    form.payment_email,
  ]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${BACKEND_URL}/api/interview-types/${slug}`);
        if (!res.ok) {
          setError(`Could not load interview (HTTP ${res.status}).`);
          setLoading(false);
          return;
        }
        const data = await res.json();
        setInterviewType(data);
      } catch (e) {
        console.error(e);
        setError("Could not load interview.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [slug]);

  function setField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  // --- Validation helpers ---
  function missingAbout() {
    const missing = [];
    if (!form.interviewee_name.trim()) missing.push("Name");
    if (!form.interviewee_email.trim() || !form.interviewee_email.includes("@"))
      missing.push("Email");
    if (!form.job_title.trim()) missing.push("Role/title");
    if (!form.company_name.trim()) missing.push("Company name");
    if (!form.company_size) missing.push("Company size");
    if (focusType !== "jtbd") {
      if (!form.decision_involvement) missing.push("Decision involvement");
    }
    return missing;
  }

  function missingPayment() {
    const missing = [];
    if (!form.payment_method) missing.push("Payment method");

    if (!isValidPaymentContact(form.payment_method, form.payment_email)) {
      missing.push("Payment email"); // keep label stable for the UI
    }

    if (!form.consent_recording) missing.push("Recording consent");
    if (!form.ack_ready) missing.push("Ready confirmation");
    return missing;
  }

  function formatMissing(missingArr) {
    if (!missingArr.length) return "";
    const show = missingArr.slice(0, 3);
    const more = missingArr.length - show.length;
    return `Still needed: ${show.join(", ")}${more > 0 ? ` +${more} more` : ""}`;
  }

  function goToStep(nextStep) {
    setStep(nextStep);
    // Keep the user oriented when advancing sections
    if (topRef?.current) {
      try {
        topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch {
        // no-op
      }
    }
  }

  function isValidPaymentContact(method, value) {
    const v = (value || "").trim();
    if (!v) return false;

    if (
      method === "paypal" ||
      method === "amazon_gift_card" ||
      method === "visa_gift_card"
    ) {
      return v.includes("@");
    }

    if (method === "venmo") {
      // Venmo can use email or phone
      const looksLikeEmail = v.includes("@");
      const looksLikePhone = /^[0-9+() -]{7,}$/.test(v);
      return looksLikeEmail || looksLikePhone;
    }

    // fallback
    return v.includes("@");
  }

  // Answering the qualifier *is* the call to action: selecting "Yes" used to
  // only enable a Continue button, which cost a second click for one decision
  // and left the page's primary action greyed out until you found the question.
  // Both funnel events still fire, at the same user moment as before.
  function handleEligibilityAnswer(answer) {
    setRecentUseAnswer(answer);

    if (answer === "no") {
      // No LinkedIn counterpart, and the most diagnostic event we record: it
      // separates "we are buying the wrong audience" from "the audience is
      // right and the offer is wrong".
      trackFunnel(
        "eligibility_failed",
        { payout_shown: payout ?? null },
        { slug }
      );
      return;
    }

    setError("");

    if (!trackedRef.current.eligibilityPassed) {
      trackedRef.current.eligibilityPassed = true;
      trackFunnel(
        "eligibility_passed",
        { payout_shown: payout ?? null },
        { slug }
      );
      trackLinkedInConversion(LINKEDIN_CONV_ID_ELIGIBILITY_PASSED);
    }

    // The old Overview screen's content now lives on the welcome screen, so its
    // "continued" moment is this answer.
    trackFunnel(
      "overview_continued",
      { payout_shown: payout ?? null },
      { slug }
    );
    trackLinkedInConversion(LINKEDIN_CONV_ID_OVERVIEW_CONTINUED);

    goToStep(STEP_DETAILS);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    const mAbout = missingAbout();
    const mPay = missingPayment();
    if (mAbout.length || mPay.length) {
      setError(`Almost there — ${formatMissing([...mAbout, ...mPay]).toLowerCase()}.`);
      return;
    }

    try {
      const payload = {
        interviewee_name: form.interviewee_name.trim(),
        interviewee_email: form.interviewee_email.trim(),

        // Ties this interview — and the Customer Story it eventually produces —
        // back to the ad and payout that brought this person here.
        visitor_id: getVisitorId(),

        payment_method: form.payment_method,
        payment_email: form.payment_email.trim(),

        acknowledgements: {
          mic_and_screen_required: true,
          understands_forced_end: true,
          ack_rules: true,
          ack_ready: true,
          consent_recording: true,
        },

        // Default TTS settings stored (InterviewPage can override later)
        tts_settings: {
          enabled: true,
          voiceName: null,
          style: "Professional",
        },

        segmentation: {
          company_name: form.company_name.trim(),
          company_size: form.company_size,
          job_title: form.job_title.trim(),
          decision_involvement: focusType === "jtbd" ? null : form.decision_involvement,
        },
      };

      const res = await fetch(
        `${BACKEND_URL}/api/interview-types/${slug}/start-interview`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(
          data?.error || `Failed to start interview (HTTP ${res.status}).`
        );
        return;
      }

      const data = await res.json();
      const interviewId = data.interview_id || data.id || data.interviewId;
      if (!interviewId) {
        setError("Interview started, but no interview ID was returned.");
        return;
      }

      trackFunnel(
        "interview_started",
        { interview_id: interviewId, payout_shown: payout ?? null },
        { slug }
      );
      // 🔥 LinkedIn conversion: "Interview Started"
      trackLinkedInConversion(LINKEDIN_CONV_ID_INTERVIEW_STARTED);

      navigate(`/interview/${interviewId}`);
    } catch (e2) {
      console.error(e2);
      setError("Failed to start interview. Please try again.");
    }
  }

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <Card>
            <div style={styles.h1}>Loading…</div>
          </Card>
        </div>
      </div>
    );
  }

  if (error && !interviewType) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <Card>
            <div style={styles.h1}>Couldn’t load this interview</div>
            <InlineAlert type="error">{error}</InlineAlert>
          </Card>
        </div>
      </div>
    );
  }

  if (!interviewType) return null;

  // Desktop gate.
  //
  // The interview needs a microphone and a full-screen share, which no mobile
  // browser can provide — so this has always been impossible on a phone. The
  // check used to live in InterviewPage.js, i.e. *after* start-interview had
  // already created an interview record and fired the INTERVIEW_STARTED
  // conversion. Since LinkedIn opens ad links in its own in-app mobile webview,
  // that meant paid clicks could walk the entire funnel, book a real interview
  // row, report a conversion, and only then hit a wall — teaching LinkedIn to
  // deliver more of an audience that structurally cannot convert.
  //
  // Turning them away here costs nothing (they could never have finished) and
  // keeps the funnel data honest. The InterviewPage check stays as a backstop.
  if (isMobile) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <Card>
            <div style={styles.kicker}>Needs a computer</div>
            <div style={styles.h1}>
              {focusType === "jtbd"
                ? `AI interview about your workflow for ${productName}`
                : `AI interview about ${productName}`}
            </div>
            {payout ? <Chip highlight>${payout} payout</Chip> : null}
            <InlineAlert type="info">
              This interview needs a microphone and screen sharing, so it has to
              be done on a laptop or desktop computer. Save the link and come
              back when you’re at one — it only takes about {estimatedMinutes}{" "}
              minutes.
            </InlineAlert>
            <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                style={styles.secondaryBtn}
                onClick={() => handleReminderClick("google")}
              >
                Add to Google Calendar
              </button>
              <button
                type="button"
                style={styles.secondaryBtn}
                onClick={() => handleReminderClick("ics")}
              >
                Download reminder
              </button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (interviewType.is_active === false) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <Card>
            <div style={styles.kicker}>
              {focusType === "jtbd" ? "Workflow under study" : "Product under study"}
            </div>
            <div style={styles.h1}>
              {focusType === "jtbd"
                ? `AI interview about your workflow for ${productName}`
                : `AI interview about ${productName}`}
            </div>
            <InlineAlert type="info">
              {focusType === "jtbd"
                ? "We’re not currently accepting further interviews for this workflow."
                : "We’re not currently accepting further interviews for this product."}
            </InlineAlert>
          </Card>
        </div>
      </div>
    );
  }

  const mAbout = missingAbout();
  const mPay = missingPayment();
  const canStart = mAbout.length === 0 && mPay.length === 0;

  const isEligible = recentUseAnswer === "yes";
  const isIneligible = recentUseAnswer === "no";

  const headline = payout
    ? focusType === "jtbd"
      ? `Get paid $${payout} to walk through how you handle ${productName}`
      : `Get paid $${payout} to talk through how you use ${productName}`
    : focusType === "jtbd"
    ? `Walk us through how you handle ${productName}`
    : `Talk us through how you use ${productName}`;

  const stickyStepLabel = step === STEP_WELCOME ? "Welcome" : "Your details";

  const stickyHelper =
    step === STEP_WELCOME
      ? isIneligible
        ? "This one needs recent experience — thanks anyway!"
        : isEligible
        ? "Great — one short form and you’re in."
        : `About ${estimatedMinutes} minutes, paid after review.`
      : canStart
      ? "Ready when you are."
      : formatMissing([...mAbout, ...mPay]);

  return (
    <div style={styles.page}>
      <style>{responsiveCss}</style>
      <div style={styles.container} ref={topRef}>
        {error ? <InlineAlert type="error">{error}</InlineAlert> : null}

        {/* SCREEN A: Welcome (landing + eligibility + overview) */}
        {step === STEP_WELCOME ? (
          <Card>
            <div className="ilp-split">
              {/* Left: the pitch */}
              <div>
                <div style={styles.kicker}>
                  {focusType === "jtbd" ? "Paid workflow research" : "Paid product research"}
                </div>
                <h1 style={styles.h1}>{headline}</h1>

                <div style={styles.metaRow}>
                  {payout ? <Chip highlight>${payout} payout</Chip> : null}
                  <Chip>~{estimatedMinutes} min</Chip>
                  <Chip>Reviewed by a human</Chip>
                </div>

                <div style={styles.introBody}>
                  An AI interviewer asks the questions — no scheduling, no live
                  moderator, done whenever suits you.
                </div>

                {/* The page's one job is to qualify, so the question is the
                    hero and answering it *is* the call to action. The old
                    "how it works" list lived here and said the same thing as
                    the preview panel beside it, in more words. */}
                <div style={styles.eligibilityBox}>
                  <div style={styles.eligibilityQuestion}>
                    {focusType === "jtbd"
                      ? `Have you done "${productName}" in the last 30 days?`
                      : `Have you used ${productName} in the last 30 days?`}
                  </div>

                  {isIneligible ? (
                    <InlineAlert type="info">
                      Thanks for your interest — this interview needs recent,
                      hands-on experience (within the last 30 days), so it isn’t
                      a fit this time. You can close this tab.
                    </InlineAlert>
                  ) : (
                    <div style={styles.segmented}>
                      <AnswerButton
                        primary
                        label="Yes — let’s go"
                        onSelect={() => handleEligibilityAnswer("yes")}
                      />
                      <AnswerButton
                        label="No"
                        onSelect={() => handleEligibilityAnswer("no")}
                      />
                    </div>
                  )}
                </div>

                {/* One line here; the full treatment lives on the next screen,
                    beside the consent checkbox, where it actually does work. */}
                <div style={styles.trustLine}>
                  Reviewed by a person before it’s shared · you control what’s on
                  screen · removable within 48 hours
                </div>

                <div style={styles.reminderRow}>
                  <span style={styles.reminderLabel}>Can’t do it right now?</span>
                  <button
                    type="button"
                    style={styles.linkBtn}
                    onClick={() => handleReminderClick("google")}
                  >
                    Add a calendar reminder
                  </button>
                  <span style={styles.reminderDot}>·</span>
                  <button
                    type="button"
                    style={styles.linkBtn}
                    onClick={() => handleReminderClick("outlook")}
                  >
                    Outlook
                  </button>
                </div>
              </div>

              {/* Right: a taste of the interview itself */}
              <div className="ilp-preview" style={styles.previewPanel} aria-hidden="true">
                <div style={styles.previewTitle}>What it feels like</div>

                <div style={styles.previewBubbleAi}>
                  <div style={styles.previewWho}>AI interviewer</div>
                  <div style={styles.previewText}>
                    {focusType === "jtbd"
                      ? `Walk me through the last time you did this. What kicked it off?`
                      : `Walk me through the last time you used ${productName}. What were you trying to get done?`}
                  </div>
                </div>

                <div style={styles.previewBubbleYou}>
                  <div style={styles.previewWhoYou}>
                    <span style={styles.previewMicDot} /> You, talking out loud
                  </div>
                  <div style={styles.previewTextYou}>
                    “Sure — so yesterday I opened it up because a client asked
                    for…”
                  </div>
                </div>

                <div style={styles.previewBubbleAi}>
                  <div style={styles.previewWho}>AI interviewer</div>
                  <div style={styles.previewText}>
                    Interesting — can you show me how you did that part?
                  </div>
                </div>

                <div style={styles.previewFoot}>
                  Self-paced · take your time on any answer
                </div>
              </div>
            </div>
          </Card>
        ) : null}

        {/* SCREEN B: Your details (about + payment + consent) */}
        {step === STEP_DETAILS ? (
          <>
            <div style={styles.detailsHeader}>
              <div>
                <div style={styles.kicker}>
                  {focusType === "jtbd" ? "Paid workflow research" : "Paid product research"}
                </div>
                <h1 style={styles.h1Small}>A little about you, and how to pay you</h1>
              </div>
              <div style={styles.metaRowTight}>
                {payout ? <Chip highlight>${payout} payout</Chip> : null}
                <Chip>~{estimatedMinutes} min</Chip>
              </div>
            </div>

            <Card>
              <form onSubmit={handleSubmit}>
                <div style={styles.sectionTitle}>About you</div>
                <div style={styles.subSmall}>
                  Used only for research — never shared with your employer.
                </div>

                <div style={styles.stack}>
                  <div className="ilp-two-col">
                    <Field label="Full name">
                      <input
                        style={styles.input}
                        value={form.interviewee_name}
                        onChange={(e) =>
                          setField("interviewee_name", e.target.value)
                        }
                        placeholder="Your name"
                        required
                      />
                    </Field>

                    <Field label="Email">
                      <input
                        style={styles.input}
                        value={form.interviewee_email}
                        onChange={(e) =>
                          setField("interviewee_email", e.target.value)
                        }
                        placeholder="you@company.com"
                        required
                      />
                    </Field>
                  </div>

                  <div className="ilp-two-col">
                    <Field label="Role / job title">
                      <input
                        style={styles.input}
                        value={form.job_title}
                        onChange={(e) => setField("job_title", e.target.value)}
                        placeholder="e.g., Product Manager"
                        required
                      />
                    </Field>

                    <Field label="Company">
                      <input
                        style={styles.input}
                        value={form.company_name}
                        onChange={(e) =>
                          setField("company_name", e.target.value)
                        }
                        placeholder="e.g., Acme Inc."
                        required
                      />
                    </Field>
                  </div>

                  <Field label="Company size">
                    <select
                      style={styles.select}
                      value={form.company_size}
                      onChange={(e) =>
                        setField("company_size", e.target.value)
                      }
                      required
                    >
                      <option value="">Select…</option>
                      {COMPANY_SIZE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </Field>

                  {focusType !== "jtbd" ? (
                    <Field label="Were you involved in the decision to use this product?">
                      <div style={styles.radioRow}>
                        <label style={styles.radioLine}>
                          <input
                            type="radio"
                            name="decision_involvement"
                            value="yes"
                            checked={form.decision_involvement === "yes"}
                            onChange={(e) =>
                              setField("decision_involvement", e.target.value)
                            }
                          />
                          Yes
                        </label>
                        <label style={styles.radioLine}>
                          <input
                            type="radio"
                            name="decision_involvement"
                            value="no"
                            checked={form.decision_involvement === "no"}
                            onChange={(e) =>
                              setField("decision_involvement", e.target.value)
                            }
                          />
                          No
                        </label>
                      </div>
                    </Field>
                  ) : null}
                </div>

                <div style={styles.divider} />

                <div style={styles.sectionTitle}>How would you like to be paid?</div>
                <div style={styles.subSmall}>
                  Sent within 2–3 business days after your interview is reviewed.
                </div>

                <div
                  style={styles.payRow}
                  role="radiogroup"
                  aria-label="Payment method"
                >
                  <PayOption
                    logoSrc={paypalLogo}
                    logoAlt="PayPal"
                    label="PayPal"
                    selected={form.payment_method === "paypal"}
                    onSelect={() => {
                      setField("payment_method", "paypal");
                      trackFunnel("payment_method_selected", { payment_method: "paypal" }, { slug });
                      trackLinkedInConversion(LINKEDIN_CONV_ID_PAYMENT_METHOD_SELECTED);
                    }}
                  />
                  <PayOption
                    logoSrc={venmoLogo}
                    logoAlt="Venmo"
                    label="Venmo"
                    selected={form.payment_method === "venmo"}
                    onSelect={() => {
                      setField("payment_method", "venmo");
                      trackFunnel("payment_method_selected", { payment_method: "venmo" }, { slug });
                      trackLinkedInConversion(LINKEDIN_CONV_ID_PAYMENT_METHOD_SELECTED);
                    }}
                  />
                  <PayOption
                    logoSrc={amazonGiftCardLogo}
                    logoAlt="Amazon Gift Card"
                    label="Amazon Gift Card"
                    selected={form.payment_method === "amazon_gift_card"}
                    onSelect={() => {
                      setField("payment_method", "amazon_gift_card");
                      trackFunnel("payment_method_selected", { payment_method: "amazon_gift_card" }, { slug });
                      trackLinkedInConversion(LINKEDIN_CONV_ID_PAYMENT_METHOD_SELECTED);
                    }}
                  />
                  <PayOption
                    logoSrc={visaGiftCardLogo}
                    logoAlt="Visa Gift Card"
                    label="Visa Gift Card"
                    selected={form.payment_method === "visa_gift_card"}
                    onSelect={() => {
                      setField("payment_method", "visa_gift_card");
                      trackFunnel("payment_method_selected", { payment_method: "visa_gift_card" }, { slug });
                      trackLinkedInConversion(LINKEDIN_CONV_ID_PAYMENT_METHOD_SELECTED);
                    }}
                  />
                </div>

                <Field label={paymentCopy.label}>
                  <input
                    style={styles.input}
                    value={form.payment_email}
                    onChange={(e) => {
                      const val = e.target.value;
                      setField("payment_email", val);

                      const interviewEmail = (
                        form.interviewee_email || ""
                      ).trim();
                      if (
                        useInterviewEmailForPayment &&
                        val.trim() !== interviewEmail
                      ) {
                        setUseInterviewEmailForPayment(false);
                      }
                    }}
                    placeholder={paymentCopy.placeholder}
                    disabled={!form.payment_method}
                  />
                  <label style={styles.checkLineCompact}>
                    <input
                      type="checkbox"
                      checked={useInterviewEmailForPayment}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setUseInterviewEmailForPayment(checked);

                        // If turning ON, copy interview email into payment email once.
                        if (checked) {
                          const interviewEmail = (
                            form.interviewee_email || ""
                          ).trim();
                          if (interviewEmail.includes("@")) {
                            setForm((prev) => ({
                              ...prev,
                              payment_email: interviewEmail,
                            }));
                          }
                        }
                      }}
                      disabled={!form.payment_method}
                    />
                    <span>Use my interview email</span>
                  </label>
                </Field>

                <div style={styles.divider} />

                <div style={styles.sectionTitle}>Before you start</div>

                {/* Moved here from the welcome screen: privacy is read when
                    someone is about to consent, not while they are still
                    deciding whether the offer is worth their time. */}
                <div style={styles.privacyBox}>
                  <div style={styles.privacyTitle}>Your privacy &amp; safety</div>
                  <ul style={styles.ul}>
                    <li>Every recording is reviewed by a person before it is shared with anyone</li>
                    <li>
                      You control what’s on screen. Before you start, close unrelated tabs
                      and avoid showing customer names, account numbers, or anything
                      confidential — we’ll remind you again when recording begins.
                    </li>
                    <li>
                      You’ll have 48 hours after processing to request edits or full
                      removal, no questions asked
                    </li>
                    <li>Your information is used only for research</li>
                  </ul>
                </div>

                <div style={styles.confirmBox}>
                  <label style={styles.checkLine}>
                    <input
                      type="checkbox"
                      checked={form.consent_recording}
                      onChange={(e) =>
                        setField("consent_recording", e.target.checked)
                      }
                    />
                    <span>
                      I consent to <strong>audio + screen recording</strong> for
                      research review.
                    </span>
                  </label>

                  <label style={styles.checkLine}>
                    <input
                      type="checkbox"
                      checked={form.ack_ready}
                      onChange={(e) =>
                        setField("ack_ready", e.target.checked)
                      }
                    />
                    <span>
                      I’m in a quiet place, ready to{" "}
                      <strong>speak out loud</strong>, and I’ll keep my{" "}
                      <strong>mic and screen share on</strong> for the whole
                      interview.
                    </span>
                  </label>
                  <div style={styles.microNoteOneLine}>
                    If your mic or screen share stops early, the interview wraps
                    up and saves what you’ve recorded so far.
                  </div>
                </div>

                {/* Optional details (collapsed) */}
                <div style={{ marginTop: 14 }}>
                  <details style={styles.details}>
                    <summary style={styles.summary}>Tips &amp; how review works</summary>

                    <div style={styles.detailsBlock}>
                      <div style={styles.detailsTitle}>
                        Tips for great answers
                      </div>
                      <div style={styles.tipGrid}>
                        <div>
                          <strong>Think out loud.</strong> Narrate what
                          you’re doing as you go.
                        </div>
                        <div>
                          <strong>Show your work.</strong> Use a real
                          example you’ve done in {productName}.
                        </div>
                        <div>
                          <strong>Don’t close this tab.</strong> Keep it
                          open so you can return after the demo.
                        </div>
                      </div>

                      <div style={styles.detailsTitle}>
                        How review + payment works
                      </div>
                      <ul style={styles.ul}>
                        <li>
                          We review interviews for clear, detailed, real
                          examples.
                        </li>
                        <li>
                          If approved, we send payment within 2–3 business
                          days.
                        </li>
                        <li>
                          If not approved, we’ll email you with a brief
                          reason.
                        </li>
                      </ul>

                      <div style={styles.detailsTitle}>
                        What will be recorded
                      </div>
                      <div style={styles.detailsText}>
                        We record your screen and audio so we can understand
                        how you use {productName}.
                      </div>
                    </div>
                  </details>
                </div>
              </form>
            </Card>
          </>
        ) : null}
      </div>

      {/* Sticky footer */}
      <div style={styles.stickyBar}>
        <div style={styles.stickyInner}>
          <div style={styles.stickyLeft}>
            <div style={styles.stickyStep}>{stickyStepLabel}</div>
            <div style={styles.stickyHelper}>{stickyHelper}</div>
          </div>

          <div style={styles.stickyRight}>
            {step === STEP_WELCOME ? (
              isIneligible ? (
                <button
                  type="button"
                  style={styles.secondaryBtn}
                  onClick={() => {
                    // Best-effort close (may be blocked by browser)
                    try {
                      window.close();
                    } catch {
                      // no-op
                    }
                  }}
                >
                  Close tab
                </button>
              ) : null
            ) : (
              <>
                <button
                  type="button"
                  style={styles.secondaryBtn}
                  onClick={() => {
                    setError("");
                    goToStep(STEP_WELCOME);
                  }}
                >
                  Back
                </button>
                <button
                  type="button"
                  style={{
                    ...styles.primaryBtn,
                    ...(canStart ? {} : styles.btnDisabled),
                  }}
                  disabled={!canStart}
                  onClick={() => {
                    // Track start interview click intent
                    trackFunnel(
                      "start_interview_clicked",
                      { payout_shown: payout ?? null },
                      { slug }
                    );
                    trackLinkedInConversion(LINKEDIN_CONV_ID_START_INTERVIEW_CLICKED);

                    const fakeEvent = { preventDefault() {} };
                    handleSubmit(fakeEvent);
                  }}
                >
                  Start interview
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Spacer so sticky bar doesn't cover content */}
      <div style={{ height: 76 }} />
    </div>
  );
}

/* ===== UI bits (page-specific; shared ones live in participant/ui) ===== */

function PayOption({ logoSrc, logoAlt, label, selected, onSelect }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => {
        onSelect();
        e.currentTarget.blur();
      }}
      style={{
        ...styles.payTile,
        ...(selected ? styles.payTileSelected : {}),
      }}
      role="radio"
      aria-checked={selected}
    >
      <img src={logoSrc} alt={logoAlt} style={styles.payLogo} draggable={false} />
      <div style={styles.payTileLabel}>{label}</div>

      {selected ? (
        <div style={styles.payTileCheck} aria-hidden="true">
          ✓
        </div>
      ) : (
        <div style={styles.payTileCheckPlaceholder} aria-hidden="true" />
      )}
    </button>
  );
}

// Plain buttons, not a radiogroup: these answer *and* advance, so announcing
// them as a selectable set would misdescribe what activating one does.
function AnswerButton({ label, primary, onSelect }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => {
        onSelect();
        e.currentTarget.blur();
      }}
      style={{
        ...styles.answerBtn,
        ...(primary ? styles.answerBtnPrimary : {}),
      }}
    >
      {label}
    </button>
  );
}

/* Layout that needs media queries — inline styles can't express them. */
const responsiveCss = `
.ilp-split {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 32px;
  align-items: start;
}
.ilp-two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
@media (max-width: 860px) {
  .ilp-split { grid-template-columns: 1fr; }
  .ilp-preview { display: none; }
}
@media (max-width: 560px) {
  .ilp-two-col { grid-template-columns: 1fr; }
}
`;

const ACCENT = colors.accent;
const ACCENT_SOFT = colors.accentSoft;

const styles = {
  ...ui,

  container: {
    maxWidth: 920,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },


  h1: {
    fontSize: 30,
    fontWeight: 700,
    color: "#111827",
    lineHeight: 1.15,
    margin: "0 0 14px",
  },
  h1Small: {
    fontSize: 22,
    fontWeight: 700,
    color: "#111827",
    lineHeight: 1.2,
    margin: 0,
  },

  metaRow: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 },
  metaRowTight: { display: "flex", gap: 8, flexWrap: "wrap" },


  subSmall: {
    fontSize: 13,
    fontWeight: 400,
    color: "#6B7280",
    marginTop: 0,
    marginBottom: 12,
    lineHeight: 1.4,
  },

  introBody: {
    fontSize: 14,
    fontWeight: 400,
    color: "#374151",
    lineHeight: 1.55,
    marginBottom: 16,
  },



  /* Eligibility — the hero of the page: its one decision. */
  eligibilityBox: {
    marginTop: 18,
    marginBottom: 16,
    padding: 18,
    borderRadius: 14,
    border: "1px solid #BFDBFE",
    background: ACCENT_SOFT,
  },
  eligibilityQuestion: {
    fontSize: 17,
    fontWeight: 650,
    color: "#111827",
    lineHeight: 1.35,
    marginBottom: 14,
  },
  answerBtn: {
    width: "100%",
    borderRadius: 12,
    border: "1px solid #D1D5DB",
    background: "#fff",
    padding: "13px 14px",
    fontSize: 15,
    fontWeight: 600,
    color: "#111827",
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(0,0,0,.04)",
  },
  answerBtnPrimary: {
    background: ACCENT,
    borderColor: ACCENT,
    color: "#fff",
  },

  segmented: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginTop: 10,
    maxWidth: 360,
  },

  /* Trust / privacy */

  trustLine: {
    fontSize: 13,
    fontWeight: 400,
    color: "#374151",
    lineHeight: 1.4,
  },

  reminderRow: {
    display: "flex",
    alignItems: "baseline",
    gap: 8,
    flexWrap: "wrap",
    marginTop: 18,
  },
  reminderLabel: { fontSize: 13, fontWeight: 500, color: "#6B7280" },
  reminderDot: { color: "#9CA3AF" },

  /* Interview preview panel (right side of welcome) */
  previewPanel: {
    borderRadius: 16,
    padding: 18,
    background: "linear-gradient(160deg, #EFF6FF 0%, #EEF2FF 55%, #F5F3FF 100%)",
    border: "1px solid #DBEAFE",
    display: "grid",
    gap: 10,
    alignSelf: "stretch",
    alignContent: "start",
  },
  previewTitle: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "#6B7280",
    marginBottom: 2,
  },
  previewBubbleAi: {
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: "12px 12px 12px 4px",
    padding: "10px 12px",
    boxShadow: "0 1px 2px rgba(0,0,0,.04)",
  },
  previewWho: {
    fontSize: 11,
    fontWeight: 600,
    color: "#6B7280",
    marginBottom: 4,
  },
  previewText: {
    fontSize: 13,
    fontWeight: 450,
    color: "#111827",
    lineHeight: 1.45,
  },
  previewBubbleYou: {
    background: ACCENT,
    borderRadius: "12px 12px 4px 12px",
    padding: "10px 12px",
    marginLeft: 18,
  },
  previewWhoYou: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    fontWeight: 600,
    color: "#DBEAFE",
    marginBottom: 4,
  },
  previewMicDot: {
    width: 7,
    height: 7,
    borderRadius: 999,
    background: "#4ADE80",
    display: "inline-block",
  },
  previewTextYou: {
    fontSize: 13,
    fontWeight: 450,
    color: "#fff",
    lineHeight: 1.45,
    fontStyle: "italic",
  },
  previewFoot: {
    fontSize: 12,
    fontWeight: 500,
    color: "#6B7280",
    textAlign: "center",
    marginTop: 4,
  },

  /* Details screen */
  detailsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 2,
  },


  stack: { display: "grid", gap: 12 },

  radioRow: { display: "flex", gap: 16, flexWrap: "wrap", marginTop: 6 },
  radioLine: {
    display: "inline-flex",
    gap: 6,
    alignItems: "center",
    fontSize: 13,
    fontWeight: 500,
    color: "#111827",
  },

  payRow: { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 },
  payTile: {
    minWidth: 160,
    boxSizing: "border-box",
    borderRadius: 12,
    border: "1px solid #D1D5DB",
    background: "#fff",
    padding: "10px 12px",
    cursor: "pointer",
    display: "flex",
    gap: 10,
    alignItems: "center",
    outline: "none",
    boxShadow: "none",
  },
  payTileSelected: {
    borderColor: ACCENT,
    background: ACCENT_SOFT,
    boxShadow: "0 0 0 3px rgba(37,99,235,.12)",
  },
  payTileLabel: { fontSize: 13, fontWeight: 600, color: "#111827" },
  payTileCheck: {
    marginLeft: "auto",
    width: 20,
    height: 20,
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: ACCENT,
    color: "#fff",
    fontWeight: 700,
    fontSize: 12,
  },
  payTileCheckPlaceholder: {
    marginLeft: "auto",
    width: 20,
    height: 20,
  },
  payLogo: {
    width: 26,
    height: 26,
    objectFit: "contain",
    borderRadius: 6,
    flex: "0 0 auto",
  },
  checkLineCompact: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    fontSize: 12,
    fontWeight: 500,
    color: "#111827",
    marginTop: 8,
  },

  privacyBox: {
    borderRadius: 12,
    border: "1px solid #E5E7EB",
    background: "#fff",
    padding: 14,
    marginTop: 8,
    marginBottom: 12,
  },
  privacyTitle: {
    fontSize: 13,
    fontWeight: 650,
    color: "#111827",
    marginBottom: 8,
  },

  confirmBox: {
    borderRadius: 12,
    border: "1px solid #E5E7EB",
    background: "#F9FAFB",
    padding: 14,
    display: "grid",
    gap: 12,
    marginTop: 8,
  },
  checkLine: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
    fontSize: 13,
    fontWeight: 500,
    color: "#111827",
    lineHeight: 1.45,
  },
  microNoteOneLine: {
    marginLeft: 26,
    marginTop: -8,
    fontSize: 12,
    fontWeight: 400,
    color: "#6B7280",
    lineHeight: 1.4,
  },




  tipGrid: { display: "grid", gap: 10, fontSize: 13, fontWeight: 400, color: "#111827", lineHeight: 1.45 },

  stickyBar: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255,255,255,.94)",
    backdropFilter: "blur(8px)",
    borderTop: "1px solid #E5E7EB",
    padding: "10px 12px",
    zIndex: 50,
  },
  stickyInner: {
    maxWidth: 920,
    margin: "0 auto",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  stickyLeft: { display: "grid", gap: 2 },
  stickyStep: { fontSize: 12, fontWeight: 650, color: "#111827" },
  stickyHelper: { fontSize: 12, fontWeight: 400, color: "#6B7280" },
  stickyRight: { display: "flex", gap: 8, alignItems: "center" },
};
